import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useGetIncident, useUpdateIncident } from '../api/queries';
import { IncidentStatus, IncidentPriority } from '../types/incident';
import { Clock, User, RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../hooks/useAuth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getStatusColor } from '../utils/incidentUtils';
import IncidentAPI from '../api/incidents';
import { getUsers } from '../api/user';
import Loader from '../components/Loader';
import Error from '../components/Error';
import EmptyState from '../components/EmptyState';
import IncidentAnalysisComponent from '../components/IncidentAnalysis';

// Define available status transitions for different roles
const itMemberTransitions: Partial<Record<IncidentStatus, IncidentStatus[]>> = {
    open: ['in_progress'], // Assuming IT can pick up open tickets
    in_progress: ['resolved'],
};

const managerTransitions: Partial<Record<IncidentStatus, IncidentStatus[]>> = {
    open: ['in_progress', 'resolved'],
    in_progress: ['resolved'],
    resolved: ['closed'],
};

export default function IncidentDetail() {
    const { id } = useParams<{ id: string }>();
    const { t } = useLanguage();
    const queryClient = useQueryClient();
    const { state: authState } = useAuth();
    const currentUser = authState.user;

    const [isEditingStatus, setIsEditingStatus] = useState(false);
    const [newStatus, setNewStatus] = useState<IncidentStatus | null>(null);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [newDescription, setNewDescription] = useState<string>('');
    const [isEditingPriority, setIsEditingPriority] = useState(false);
    const [newPriority, setNewPriority] = useState<IncidentPriority | null>(null);
    const [isAssigning, setIsAssigning] = useState(false);
    const [selectedAssignee, setSelectedAssignee] = useState<string | null>(null);
    const [analysisList, setAnalysisList] = useState<any[]>([]);
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [analysisError, setAnalysisError] = useState<string | null>(null);

    const { data: incident, isLoading: isIncidentLoading, error: incidentError } = useGetIncident(id!);
    const { mutate: updateIncident, isPending: isUpdating } = useUpdateIncident();
    const { data: users } = useQuery({ queryKey: ['users'], queryFn: getUsers, enabled: !!incident });

    const isItMember = currentUser?.roles.includes('it-member');
    const isManager = currentUser?.roles.includes('incidence-manager');
    const canUpdateStatus = isItMember || isManager;

    const getAvailableStatuses = () => {
        if (!incident) return [];
        if (isManager) return managerTransitions[incident.status] || [];
        if (isItMember) return itMemberTransitions[incident.status] || [];
        return [];
    };

    const availableStatuses = getAvailableStatuses();

    const handleStatusUpdate = () => {
        if (incident && newStatus) {
            updateIncident({ id: incident.id, data: { status: newStatus } }, {
                onSuccess: () => {
                    setIsEditingStatus(false);
                    queryClient.invalidateQueries({ queryKey: ['incident', incident.id] });
                    queryClient.invalidateQueries({ queryKey: ['incidents'] }); // Also invalidate the list view
                }
            });
        }
    };

    const handleDescriptionUpdate = () => {
        if (incident && newDescription) {
            updateIncident({ id: incident.id, data: { description: newDescription } }, {
                onSuccess: () => {
                    setIsEditingDescription(false);
                    queryClient.invalidateQueries({ queryKey: ['incident', incident.id] });
                    queryClient.invalidateQueries({ queryKey: ['incidents'] });
                }
            });
        }
    };

    const handlePriorityUpdate = () => {
        if (incident && newPriority) {
            updateIncident({ id: incident.id, data: { priority: newPriority } }, {
                onSuccess: () => {
                    setIsEditingPriority(false);
                    queryClient.invalidateQueries({ queryKey: ['incident', incident.id] });
                    queryClient.invalidateQueries({ queryKey: ['incidents'] });
                }
            });
        }
    };

    useEffect(() => {
        setNewStatus(null); // Reset selection when form is opened/closed
    }, [isEditingStatus]);

    useEffect(() => {
        if (incident) {
            setSelectedAssignee(incident.assigned_to || null);
        }
    }, [incident]);

    const assignableItMembers = (users || []).filter((u: any) => (u.roles || []).includes('it-member') && (currentUser?.roles.includes('super-admin') ? true : u.company_id === currentUser?.company_id));

    const handleAssign = () => {
        if (!incident) return;
        const payload: any = { assign_to_user_id: selectedAssignee };
        updateIncident({ id: incident.id, data: payload }, {
            onSuccess: () => {
                setIsAssigning(false);
                queryClient.invalidateQueries({ queryKey: ['incident', incident.id] });
                queryClient.invalidateQueries({ queryKey: ['incidents'] });
            }
        });
    };

    useEffect(() => {
        if (incident && isEditingDescription) {
            setNewDescription(incident.description);
        }
    }, [isEditingDescription, incident]);

    useEffect(() => {
        if (incident && isEditingPriority) {
            setNewPriority(incident.priority);
        }
    }, [isEditingPriority, incident]);

    const fetchIncidentSuggestions = useCallback(async () => {
        if (!incident) return;
        setAnalysisLoading(true);
        setAnalysisError(null);
        try {
            const suggestions = await IncidentAPI.getIncidentSuggestions(incident.id);
            setAnalysisList(suggestions);
        } catch (err: any) {
            const errorMessage = err?.message || 'Failed to fetch AI suggestions';
            setAnalysisError(errorMessage);
        } finally {
            setAnalysisLoading(false);
        }
    }, [incident]);

    useEffect(() => {
        if (incident && analysisList.length === 0 && !analysisLoading && !analysisError) {
            fetchIncidentSuggestions();
        }
    }, [fetchIncidentSuggestions, analysisList.length, analysisLoading, analysisError, incident]);

    if (isIncidentLoading) return <Loader />;
    if (incidentError) return <Error message={`${t('common.error')}: ${incidentError instanceof Error ? incidentError.message : String(incidentError)}`} />;
    if (!incident) return <EmptyState title={t('incidentDetail.notFound')} message={t('incidentDetail.notFoundMsg')} />;

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="p-8">
                    <div className="flex justify-between items-start mb-8">
                        <div className="flex-1">
                            <h1 className="text-4xl font-bold mb-4">{incident.title}</h1>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                                {/* Status - Editable */}
                                <div 
                                    className="space-y-2 cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors"
                                    onClick={() => canUpdateStatus && setIsEditingStatus(true)}
                                >
                                    <p className="text-sm text-gray-500 uppercase tracking-wide">Status {canUpdateStatus && <span className="text-xs text-blue-500">(click to edit)</span>}</p>
                                    {isEditingStatus ? (
                                        <div className="flex gap-2">
                                            <select 
                                                value={newStatus || incident.status}
                                                onChange={(e) => setNewStatus(e.target.value as IncidentStatus)}
                                                className="block p-2 border border-gray-300 rounded-md text-sm"
                                                onBlur={() => {
                                                    if (newStatus && newStatus !== incident.status) {
                                                        handleStatusUpdate();
                                                    } else {
                                                        setIsEditingStatus(false);
                                                    }
                                                }}
                                                autoFocus
                                            >
                                                {availableStatuses.map(status => (
                                                    <option key={status} value={status}>{status.toUpperCase()}</option>
                                                ))}
                                            </select>
                                        </div>
                                    ) : (
                                        <span className={`inline-block px-4 py-2 text-sm font-semibold rounded-full ${getStatusColor(incident.status)}`}>{incident.status.toUpperCase()}</span>
                                    )}
                                </div>

                                {/* Priority - Editable */}
                                <div 
                                    className="space-y-2 cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors"
                                    onClick={() => isManager && setIsEditingPriority(true)}
                                >
                                    <p className="text-sm text-gray-500 uppercase tracking-wide">Priority {isManager && <span className="text-xs text-blue-500">(click to edit)</span>}</p>
                                    {isEditingPriority ? (
                                        <select 
                                            value={newPriority || incident.priority}
                                            onChange={(e) => setNewPriority(e.target.value as IncidentPriority)}
                                            className="block p-2 border border-gray-300 rounded-md text-sm"
                                            onBlur={() => {
                                                if (newPriority && newPriority !== incident.priority) {
                                                    handlePriorityUpdate();
                                                } else {
                                                    setIsEditingPriority(false);
                                                }
                                            }}
                                            autoFocus
                                        >
                                            <option value="low">LOW</option>
                                            <option value="medium">MEDIUM</option>
                                            <option value="high">HIGH</option>
                                            <option value="critical">CRITICAL</option>
                                        </select>
                                    ) : (
                                        <p className={`text-lg font-bold ${incident.priority === 'critical' ? 'text-red-600' : incident.priority === 'high' ? 'text-orange-600' : incident.priority === 'medium' ? 'text-blue-600' : 'text-gray-600'}`}>{incident.priority.toUpperCase()}</p>
                                    )}
                                </div>

                                {/* Created - Not Editable */}
                                <div className="space-y-2">
                                    <p className="text-sm text-gray-500 uppercase tracking-wide">Created</p>
                                    <div className="flex items-center space-x-2">
                                        <Clock size={18} className="text-gray-400" />
                                        <p className="text-gray-700">{new Date(incident.created_at).toLocaleString()}</p>
                                    </div>
                                </div>

                                {/* Created By - Not Editable */}
                                <div className="space-y-2">
                                    <p className="text-sm text-gray-500 uppercase tracking-wide">Created By</p>
                                    <div className="flex items-center space-x-2">
                                        <User size={18} className="text-gray-400" />
                                        <p className="text-gray-700">{incident.user_email }</p>
                                    </div>
                                </div>
                                {/* Assigned To - Editable by managers */}
                                <div 
                                    className={`space-y-2 p-3 rounded-lg transition-colors ${isManager ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                                    onClick={() => {
                                        if (!isManager) return;
                                        if (analysisLoading || analysisList.length === 0) return;
                                        setIsAssigning(true);
                                    }}
                                >
                                    <p className="text-sm text-gray-500 uppercase tracking-wide">Assigned To {isManager && <span className="text-xs text-blue-500">(click to assign)</span>}</p>
                                    {isAssigning ? (
                                        <div className="flex gap-2">
                                            <select 
                                                value={selectedAssignee || ''}
                                                onChange={(e) => setSelectedAssignee(e.target.value || null)}
                                                className="block p-2 border border-gray-300 rounded-md text-sm"
                                                autoFocus
                                            >
                                                <option value="">Unassigned</option>
                                                {assignableItMembers.map((u: any) => (
                                                    <option key={u.id} value={u.id}>{`${u.name || u.email} (${u.it_role || (u.roles || []).join(', ')})`}</option>
                                                ))}
                                            </select>
                                            <button onClick={handleAssign} className="px-3 py-1 bg-blue-600 text-white rounded-md">Assign</button>
                                            <button onClick={() => { setIsAssigning(false); setSelectedAssignee(incident.assigned_to || null); }} className="px-3 py-1 bg-gray-200 rounded-md">Cancel</button>
                                        </div>
                                    ) : (
                                        <p className="text-gray-700">{
                                            (() => {
                                                const assigneeUser = (users || []).find((u: any) => u.id === incident.assigned_to);
                                                return assigneeUser ? `${assigneeUser.name || assigneeUser.email} (${assigneeUser.it_role || (assigneeUser.roles || []).join(', ')})` : (incident.assigned_to ? incident.assigned_to : 'Unassigned');
                                            })()
                                        }</p>
                                    )}
                                    {isManager && (analysisLoading || analysisList.length === 0) && (
                                        <p className="text-xs text-gray-500 mt-1">Assignment available after AI analysis finishes.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t pt-8">
                        <h2 className="text-2xl font-semibold mb-4">Description</h2>
                        {isEditingDescription ? (
                            <div className="space-y-4">
                                <textarea 
                                    value={newDescription}
                                    onChange={(e) => setNewDescription(e.target.value)}
                                    className="block w-full p-3 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                    rows={6}
                                    placeholder="Enter the incident description..."
                                />
                                <div className="flex justify-end space-x-2">
                                    <button onClick={() => setIsEditingDescription(false)} className="px-4 py-2 bg-gray-200 rounded-md text-sm font-medium hover:bg-gray-300">Cancel</button>
                                    <button onClick={handleDescriptionUpdate} disabled={!newDescription || isUpdating} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium disabled:bg-gray-400">
                                        {isUpdating ? 'Updating...' : 'Save'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <p 
                                onClick={() => isManager && setIsEditingDescription(true)}
                                className={`text-gray-700 whitespace-pre-wrap leading-relaxed ${isManager ? 'cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors' : ''}`}
                            >
                                {incident.description}
                                {isManager && <span className="block text-xs text-blue-500 mt-2">(click to edit)</span>}
                            </p>
                        )}
                    </div>

                    {/* Analysis Section */}
                    {analysisError && (
                        <div className="border-t mt-8 pt-8">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-red-800">Analysis Failed</h3>
                                </div>
                                <p className="text-red-700 mb-4">
                                    {analysisError.includes('no embedding') 
                                        ? 'AI analysis is being prepared for this incident. Please try again in a moment.'
                                        : analysisError
                                    }
                                </p>
                                <button
                                    onClick={fetchIncidentSuggestions}
                                    disabled={analysisLoading}
                                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium transition-colors"
                                >
                                    <RefreshCw size={16} className={analysisLoading ? 'animate-spin' : ''} />
                                    <span>{analysisLoading ? 'Retrying...' : 'Retry AI Analysis'}</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {analysisLoading && (
                        <div className="border-t mt-8 pt-8">
                            <div className="flex items-center space-x-3">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                                <p className="text-gray-600">Generating AI suggestions...</p>
                            </div>
                        </div>
                    )}

                    {analysisList.length > 0 && !analysisLoading && (
                        <div className="border-t mt-8 pt-8">
                            <h2 className="text-2xl font-semibold mb-6">AI Analysis</h2>
                            {analysisList.map((analysis, index) => (
                                <div key={index}>
                                    <IncidentAnalysisComponent analysis={analysis} incidentId={incident.id} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
